import React, { ReactNode } from 'react';
import styled, { css } from 'styled-components';
import {
  Flex,
  IconButton,
  IconButtonProps,
  isDarkThemeMixin,
  isMobileStyleMixin,
  isNotDarkThemeMixin,
  styleMixinInvertedFocusOutline,
} from '@amzn/storm-ui';
import { IconDefinition } from '@amzn/storm-ui-icons';
import { PAGE_LAYOUT_TYPE } from 'src/app/slice';

interface UtilityButtonProps extends Omit<IconButtonProps, 'variant'> {
  /** The icon to display in the button */
  icon: IconDefinition;
  /** Accessible label for the button */
  label: string;
  /** Whether to show a badge indicator */
  withBadge?: boolean;
  /** Number to display in the badge */
  badgeCounter?: number;
}

const UtilityButtonWrapper = styled(Flex)`
  position: relative;
`;

const UtilityIconButton = styled(IconButton).attrs({
  variant: 'transparent',
})`
  width: 40px;
  height: 40px;
  flex-shrink: 0;

  svg {
    font-size: 24px;

    ${isMobileStyleMixin(css`
      font-size: 20px;
    `)};
  }

  && {
    :hover,
    :active {
      background-color: ${({ theme }) => theme.color.gray[1000]};
    }

    ${isNotDarkThemeMixin(css`
      svg {
        color: ${({ theme }) => theme.icon.color.inverted};
      }

      &:hover svg {
        color: ${({ theme }) => theme.icon.color.invertedHover};
      }

      &:active svg {
        color: ${({ theme }) => theme.icon.color.invertedActive};
      }

      :focus-visible {
        ${styleMixinInvertedFocusOutline};
      }
    `)};

    ${isDarkThemeMixin(css`
      svg {
        color: ${({ theme }) => theme.icon.color.default};
      }

      &:hover svg {
        color: ${({ theme }) => theme.icon.color.secondary};
      }

      &:active svg {
        color: ${({ theme }) => theme.icon.color.tertiary};
      }
    `)};
  }
`;

const StyledBadge = styled(Flex).attrs({
  justifyContent: 'center',
  alignItems: 'center',
})<{ withCounter?: boolean }>`
  font-size: 10px;
  background-color: ${({ theme }) => theme.color.red[700]};
  color: ${({ theme }) =>
    theme.header.contextBar[PAGE_LAYOUT_TYPE.STANDARD].textColor};
  border-radius: ${({ theme }) => theme.border.radius.round};
  position: absolute;
  right: 3px;
  top: 3px;
  width: 12px;
  height: 12px;

  ${({ withCounter }) =>
    withCounter &&
    css`
      width: 15px;
      height: 15px;
      right: 1px;
      top: 1px;
    `}
`;

const Badge = ({
  withCounter,
  children,
}: {
  withCounter?: boolean;
  children?: ReactNode;
}) => {
  return <StyledBadge withCounter={withCounter}>{children}</StyledBadge>;
};

const UtilityButton = ({
  icon,
  label,
  withBadge = false,
  badgeCounter,
  ...rest
}: UtilityButtonProps) => {
  return (
    <UtilityButtonWrapper>
      <UtilityIconButton icon={icon} label={label} {...rest} />
      {withBadge && <Badge withCounter={!!badgeCounter}>{badgeCounter}</Badge>}
    </UtilityButtonWrapper>
  );
};

export default UtilityButton;
